const API_BASE = import.meta.env.VITE_API_URL ?? '/api'

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })
  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: 'Unknown error' }))
    throw new Error(error.error ?? error.message ?? `HTTP ${res.status}`)
  }
  return res.json()
}

export const api = {
  contracts: {
    list: (token: string, workspaceId?: string) => {
      const path = workspaceId ? `/contracts?workspaceId=${workspaceId}` : '/contracts'
      return request<{ contracts: import('@/types').Contract[] }>(path, {
        headers: { Authorization: `Bearer ${token}` },
      })
    },
    get: (id: string, token: string) =>
      request<import('@/types').ContractWithClauses>(`/contracts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    delete: (id: string, token: string) =>
      request(`/contracts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }),
    compare: (contractIdA: string, contractIdB: string, token: string) =>
      request<import('@/types').ContractComparison>('/compare', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contractIdA, contractIdB }),
      }),
  },
  analyze: {
    upload: async (file: File, preferredLanguage: string, token: string) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('preferredLanguage', preferredLanguage)
      const res = await fetch(`${API_BASE}/analyze`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })
      if (!res.ok) {
        const error = await res.json().catch(() => ({ message: 'Upload failed' }))
        throw new Error(error.error ?? error.message ?? 'Upload failed')
      }
      return res.json() as Promise<{ contractId: string }>
    },
  },
  user: {
    me: (token: string) =>
      request<import('@/types').User>('/user/me', {
        headers: { Authorization: `Bearer ${token}` },
      }),
    updatePreferences: (token: string, prefs: { preferredLanguage?: string; emailNotifications?: boolean }) =>
      request('/user/me', {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(prefs),
      }),
    delete: (token: string) =>
      request<{ ok: true }>('/user/me', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }),
  },
  billing: {
    checkout: (token: string, body: { priceId: string; successUrl: string; cancelUrl: string }) =>
      request<{ url: string }>('/billing/checkout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      }),
    payPerUse: (token: string, body: { successUrl: string; cancelUrl: string }) =>
      request<{ url: string }>('/billing/pay-per-use', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      }),
    portal: (token: string) =>
      request<{ url: string }>('/billing/portal', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }),
  },
  workspaces: {
    list: (token: string) =>
      request<{ workspaces: import('@/types').WorkspaceWithRole[] }>('/workspaces', {
        headers: { Authorization: `Bearer ${token}` },
      }),
    create: (token: string, body: { name: string }) =>
      request<{ workspace: import('@/types').Workspace; membership: import('@/types').WorkspaceMembership }>(
        '/workspaces',
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        },
      ),
    get: (id: string, token: string) =>
      request<{
        workspace: import('@/types').Workspace & { memberships: import('@/types').WorkspaceMemberWithUser[] }
      }>(`/workspaces/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    update: (id: string, token: string, body: { name: string }) =>
      request<{ workspace: import('@/types').Workspace }>(`/workspaces/${id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      }),
    delete: (id: string, token: string) =>
      request<{ success: true }>(`/workspaces/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }),
    listMembers: (id: string, token: string) =>
      request<{ members: import('@/types').WorkspaceMemberWithUser[] }>(`/workspaces/${id}/members`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    inviteMember: (id: string, token: string, body: { email: string; role: import('@/types').InvitableRole }) =>
      request<{ membership: import('@/types').WorkspaceMembership }>(`/workspaces/${id}/members`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      }),
    updateMemberRole: (
      id: string,
      memberId: string,
      token: string,
      body: { role: import('@/types').InvitableRole },
    ) =>
      request<{ membership: import('@/types').WorkspaceMembership }>(`/workspaces/${id}/members/${memberId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      }),
    removeMember: (id: string, memberId: string, token: string) =>
      request<{ success: true }>(`/workspaces/${id}/members/${memberId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }),
    getActivity: (id: string, token: string) =>
      request<{ activity: import('@/types').ActivityLog[] }>(`/workspaces/${id}/activity`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    updateRiskThresholds: (id: string, token: string, thresholds: import('@/types').RiskThreshold[]) =>
      request<{ workspace: import('@/types').Workspace }>(`/workspaces/${id}/risk-thresholds`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({ thresholds }),
      }),
  },
  shares: {
    create: (contractId: string, token: string, body?: { expiresAt?: string }) =>
      request<{ shareLink: import('@/types').ContractShareLink; url: string }>(
        `/contracts/${contractId}/share`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: JSON.stringify(body ?? {}),
        },
      ),
    list: (contractId: string, token: string) =>
      request<{ shareLinks: import('@/types').ContractShareLink[] }>(`/contracts/${contractId}/shares`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    revoke: (token_: string, token: string) =>
      request<{ success: true }>(`/shares/${token_}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }),
    getPublic: (shareToken: string) =>
      request<{
        contract: import('@/types').ContractWithClauses
        shareLink: { token: string; expiresAt: string | null; viewCount: number }
      }>(`/public/shared/${shareToken}`),
  },
  webhookEndpoints: {
    list: (token: string) =>
      request<import('@/types').WebhookEndpoint[]>('/webhook-endpoints', {
        headers: { Authorization: `Bearer ${token}` },
      }),
    create: (token: string, body: { url: string }) =>
      request<import('@/types').WebhookEndpoint>('/webhook-endpoints', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      }),
    update: (id: string, token: string, body: { url?: string; enabled?: boolean }) =>
      request<import('@/types').WebhookEndpoint>(`/webhook-endpoints/${id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      }),
    delete: (id: string, token: string) =>
      request<{ ok: true }>(`/webhook-endpoints/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }),
  },
  apiKeys: {
    list: (token: string) =>
      request<import('@/types').ApiKey[]>('/api-keys', {
        headers: { Authorization: `Bearer ${token}` },
      }),
    create: (token: string, body: { name: string }) =>
      request<{ rawKey: string; prefix: string; name: string }>('/api-keys', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      }),
    delete: (id: string, token: string) =>
      request<{ ok: boolean }>(`/api-keys/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }),
  },
  clauseLibrary: {
    list: () => request<{ clauseTypes: string[] }>('/public/clause-library'),
    get: (clauseType: string) =>
      request<{ clause: import('@/types').LibraryClause }>(`/public/clause-library/${clauseType}`),
  },
}
